import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Paper, Tooltip, IconButton, Typography, Fab } from '@material-ui/core';
import { Add, Refresh, Delete } from '@material-ui/icons';
import { hasPermission, hasAtLeastOnePermission, BackButton, localization, getDimensionsOfPagination } from '../../sc-utilities';
import NetworkZoneDelete from './NetworkZoneDelete';
import * as actions from '../store/networkZoneActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import { CancelToken } from 'axios';

const styles = theme => ({
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const NetworkZoneList = props => {
    const { classes, onFetchNetworkZones, onFetchNetworkZonesCount, tableColumns, fetching, onBulkDeleteNetworkZones, redirectHandler, saveColumnsFilter, activeCustomerID, translations, permissions, deleting, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, networkZoneIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['network_zone_delete']);
    const deletePermission = hasPermission(permissions, 'network_zone_delete');

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = false;
            if (!hasPermission(permissions, 'network_zone_list')) {
                if (!deletePermission) {
                    disabled = true;
                }
            }
            rowData.tableData.disabled = disabled;
            return disabled ? {
                disabled: true,
                id: `checkbox_${rowData.NetworkZoneID}`,
                checked: false
            } : { id: `checkbox_${rowData.NetworkZoneID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        exportFileName: 'Network Zone',
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchNetworkZonesCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await onFetchNetworkZones(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['network_zones'] || 'Network zones'}</Typography>
                {
                    hasPermission(permissions, 'network_zone_create') ?
                        <Fab
                            id="create_network_zone"
                            variant="extended"
                            size="small"
                            color="primary"
                            aria-label="Add"
                            style={{ borderRadius: '1px' }}
                            className={classes.fab}
                            onClick={() => redirectHandler(`/network-zone/create`)}
                        >
                            <Add size="small" />
                            {translations["create_new"] || "Create new"}
                        </Fab> : null
                }
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, networkZoneIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteNetworkZones({ NetworkZoneIDs: deleteModalState.networkZoneIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, networkZoneIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = networkZoneIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, networkZoneIDs: networkZoneIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["network_zones"] || "Network Zones"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(networkZone => networkZone.NetworkZoneID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`NetworkZone`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                actions={tableActions}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'network_zone_delete') ? (
                    <NetworkZoneDelete
                        entityName={translations['network_zone'] || 'Network zone'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        deleteModalState={deleteModalState}
                        deleting={deleting}
                    />
                ) : null
            }
        </Paper>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        deleting: state.networkZone.networkZones.deleting,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.networkZone.networkZones.fetching
    };
}

const mapDispatchToProps = dispatch => {
    return {
        onFetchNetworkZonesCount: (filterParams, options) => dispatch(actions.fetchNetworkZoneCount(filterParams, options)),
        onFetchNetworkZones: (filterParams, options) => dispatch(actions.fetchNetworkZone(filterParams, options)),
        onBulkDeleteNetworkZones: networkZone => dispatch(actions.bulkDeleteNetworkZones(networkZone)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(NetworkZoneList));